import * as React from 'react';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import { History } from 'history';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Icons } from '../../../resources';
import { ReactComponent as HomeLocationIcon } from '../../../resources/icons/HomeLocationIcon.svg';
import { trackHomePageNavButton } from '../../../Core/Tracking';
import {
  canAccessHotDeskFeature,
  canReserveAtHomeLocationOnly,
  getIsVirtualMember,
} from '../../../Core/Utils/userPermissions';
import {
  getUserGroupings,
  getUserGroupingsIsLoading,
} from '../../../Redux/Common/Groupings/selectors';
import { getUserProfile } from '../../../Redux/Common/UserManager/selectors';
import { DESK_LANDING_PATH } from '../../DeskReservationLanding/constants';
import { OPEN_WORKSPACE_LANDING_PATH } from '../../OpenWorkspace/constants';
import { RESERVE_OFFICE_PATH } from '../../ReserveOffice/reserveOfficeConstants';
import { ROOM_BOOKING_PATH } from '../../RoomBooking/constants';
import ReserveButton from './ReserveButton';

interface Props {
  history: History;
  banner?: React.ReactNode;
  classes: Record<string, string>;
}

const ReserveOptions = ({ history, banner = null, classes }: Props) => {
  const { t, i18n } = useTranslation();

  const profile = useSelector(getUserProfile);
  const userGroupings = useSelector(getUserGroupings);
  const userGroupingsIsLoading = useSelector(getUserGroupingsIsLoading);

  const goToReserveDesk = () => {
    trackHomePageNavButton('Desk Reservations');
    history.push(DESK_LANDING_PATH);
  };

  const goToReserveOffice = () => {
    trackHomePageNavButton('Office Reservation');
    history.push(RESERVE_OFFICE_PATH);
  };

  const goToMeetingRoomsAtHome = () => {
    trackHomePageNavButton('Meeting Room At Home Location');
    window.open(ROOM_BOOKING_PATH, '_self');
  };

  const goToMeetingRoomsAtAnotherLocation = () => {
    trackHomePageNavButton('Meeting Room At Another Location');
    const langPrefix = i18n.language !== 'en' ? `${i18n.language}/` : '';
    window.open(`${langPrefix}home/roombooking`);
  };

  const goToDayPass = () => {
    trackHomePageNavButton('Day Pass Reservation');
    window.open(OPEN_WORKSPACE_LANDING_PATH, '_self');
  };

  const showHotDeskCard = canAccessHotDeskFeature(
    profile,
    userGroupings,
    userGroupingsIsLoading
  );

  const cardSizeLG = showHotDeskCard ? 6 : 4;
  const cardSizeXL = showHotDeskCard ? 3 : 4;

  const userIsVirtual = getIsVirtualMember(profile);
  const reserveAtHomeLocationOnly = canReserveAtHomeLocationOnly(profile);

  return (
    <>
      <Grid item xs={12} md={6} lg={cardSizeLG} xl={cardSizeXL}>
        <ReserveButton
          cta={t('home.navigation_ctas.conference.cta')}
          description={t('home.navigation_ctas.conference.description')}
          iconSrc={Icons.HomeConferenceNav}
        >
          <Button
            key="at-home"
            className={classes.meetingSpaceSubButtonAtHome}
            onClick={goToMeetingRoomsAtHome}
          >
            <HomeLocationIcon className={classes.meetingSpaceSubButtonIcon} />
            {t('home.navigation_ctas.conference.at_home_location')}
          </Button>
          {!reserveAtHomeLocationOnly && (
            <Button
              key="at-another"
              className={classes.meetingSpaceSubButtonAtAnother}
              onClick={goToMeetingRoomsAtAnotherLocation}
              disableRipple
            >
              {t('home.navigation_ctas.conference.at_another_location')}
            </Button>
          )}
        </ReserveButton>
      </Grid>
      <Grid item xs={12} md={6} lg={cardSizeLG} xl={cardSizeXL}>
        <ReserveButton
          cta={t('home.navigation_ctas.office.cta')}
          description={t('home.navigation_ctas.office.description')}
          onClick={goToReserveOffice}
          iconSrc={Icons.HomeOfficeNav}
          isDisabled={userIsVirtual}
          tooltipText={
            userIsVirtual ? t('home.navigation_ctas.virtual_tooltip') : null
          }
        />
      </Grid>
      {showHotDeskCard && (
        <Grid item xs={12} md={6} lg={cardSizeLG} xl={cardSizeXL}>
          <ReserveButton
            cta={t('home.navigation_ctas.desk.cta')}
            description={t('home.navigation_ctas.desk.description')}
            onClick={goToReserveDesk}
            iconSrc={Icons.HomeDeskNav}
            isBeta
          />
        </Grid>
      )}
      {!userIsVirtual && (
        <Grid
          item
          xs={12}
          md={6}
          lg={cardSizeLG}
          xl={cardSizeXL}
          style={{ position: 'relative', zIndex: 1, overflow: 'visible' }}
        >
          <ReserveButton
            cta={t('home.navigation_ctas.openWorkspace.cta')}
            description={t('home.navigation_ctas.openWorkspace.description')}
            onClick={goToDayPass}
            iconSrc={Icons.HomeSeatNav}
          />
          {banner}
        </Grid>
      )}
    </>
  );
};

ReserveOptions.defaultProps = {
  banner: null,
};

const styles = {
  meetingSpaceSubButtonAtHome: {
    border: '1px solid #c8c5bf',
    borderRadius: 8,
    padding: '6px 14px',
    background: 'transparent',
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '1em',
    color: '#013E3F',
    minWidth: 0,
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.04)',
      border: '1px solid #c8c5bf',
    },
    '@media (max-width: 479.95px)': {
      marginTop: 5,
    },
  },
  meetingSpaceSubButtonAtAnother: {
    border: 'none',
    background: 'transparent',
    boxShadow: 'none',
    padding: '6px 0',
    marginLeft: 8,
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '1em',
    color: '#013E3F',
    minWidth: 0,
    '&:hover': {
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
    },
    '@media (max-width: 479.95px)': {
      marginLeft: 2,
      marginTop: 5,
    },
  },
  meetingSpaceSubButtonIcon: {
    marginRight: 6,
    flexShrink: 0,
    width: 14,
    height: 14,
  },
};

export default withStyles(styles)(ReserveOptions);
